import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, firstValueFrom } from "rxjs";
import { Constants } from "../../../../constants";
import DataService from "../../../Common/services/data.service";
import DialogService from "../../../Common/services/dialog.service";
import ExceptionService from "../../../Common/services/exception.service";
import LocationHistoryService from "../../../Common/services/locationHistory.service";
import ToastService from "../../../Common/services/toast.service";
import { ArcusAuthService } from "../../../core/arcusauth.service";

export interface ILockable {
    Locked: boolean;
    LockUserID: string;
    LockUserName: string;
    LockDateTime: Date;
}
@Injectable({ providedIn: "root" })
export default class LockService {
    readonly lockInterval: number = 30000;
    private type: string;
    private id: number;
    private currentLock: any = null;
    private lockError: string = null;
    logoutSource = new BehaviorSubject<boolean>(false);
    $logoutValue = this.logoutSource.asObservable();

    constructor(
        private authService: ArcusAuthService,
        private locationHistoryService: LocationHistoryService,
        private dataService: DataService,
        private dialogService: DialogService,
        private exceptionService: ExceptionService,
        private toastr: ToastService,
        private router: Router
    ) {}

    createOrderLock = (id: number): void => this.createLock("Order", id);

    createLoadLock = (id: number): void => this.createLock("Load", id);

    createLock = (type: string, id: number, maintain: boolean = true): void => {
        this.type = type;
        this.id = id;
        this.dataService
            .get("api/ILockable/" + this.type + "/Lock/" + this.id)
            .then((result) => {
                if (!result) this.toastr.warning("Could not lock all related assets");
            })
            .catch((error) => {
                if (error) {
                    this.lockError = error;
                    this.dialogService
                        .mdAlert(error, "Locked", "Ok", "lock")
                        .afterClosed()
                        .subscribe(() => {
                            window.history.back();
                        });
                    return;
                } else {
                    this.exceptionService.handleException(error);
                }
            });

        if (maintain) this.maintainLock();
    };

    removeLock = async () => {
        this.cancelLock();

        if (!this.type || !this.id) return;

        const token = await firstValueFrom(this.authService.getAccessToken());

        fetch(`${Constants.apiEndpoint}api/ILockable/${this.type}/Unlock/${this.id}`, {
            keepalive: true,
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        }).then((res) => {
            return res?.ok ? Promise.resolve(null) : this.exceptionService.handleException(res?.statusText);
        });
    };

    maintainLock = (): void => {
        this.cancelLock();

        if (!this.type || !this.id) return;

        this.currentLock = setInterval(() => {
            this.dataService.get("api/ILockable/" + this.type + "/MaintainLock/" + this.id).then((result) => {
                if (!result) {
                    this.cancelLock();
                    this.dialogService
                        .mdAlert(
                            this.lockError ||
                                "This record has been locked by another user. You are no longer able to edit this record.",
                            "Locked",
                            "Ok"
                        )
                        .afterClosed()
                        .subscribe((result) => {
                            if (this.lockError != null) {
                                window.history.back();
                            } else {
                                this.router.navigate([this.locationHistoryService.getPreviousUrl()]);
                            }
                        });
                }
            });
        }, this.lockInterval);
    };

    cancelLock = (): void => {
        if (this.currentLock !== null) {
            clearInterval(this.currentLock);
        }
    };
}
